import { ReactNode, SyntheticEvent } from 'react';
import {
  Autocomplete as MUIAutocomplete,
  AutocompleteProps as MUIAutocompleteProps,
  Popper as MUIPopper,
  SxProps,
} from '@mui/material';

import {
  Icon,
  TextField,
  TextFieldProps as TextFieldPropsType,
} from '@atom/mui';
import { CloseIcon } from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import fonts from '@atom/styles/fonts';

type CustomProps = {
  label?: string;
  startAdornment?: ReactNode;
  endAdornment?: ReactNode;
  /**
   * popperAutoWidth if true will cause the select list's
   * width to grow dynamically with the content of each list item
   */
  popperAutoWidth?: boolean;
  /**
   * Anchor the options popper to a specific element instead of the input
   * (e.g. a wrapping container). Defaults to the input.
   */
  popperAnchorEl?: Element;
  /** Fixed width applied to the options popper. */
  popperWidth?: string | number;
  autoFocus?: boolean;
  error?: boolean;
  errorHelperText?: string;
  TextFieldProps?: TextFieldPropsType;
  placeholder?: string;
  disableUnderline?: boolean;
  sx?: SxProps;
};

type AtomMUIProps = {
  id?: string;
  disableClearable?: boolean;
  clearIcon?: ReactNode;
  classes?: any;
  multiple?: boolean;
  loading?: boolean;
  disabled?: boolean;
  inputValue?: string;
  onInputChange?: (
    event: SyntheticEvent<any>,
    value: string,
    reason: string,
  ) => void;
  getOptionLabel?: (option: any) => string;
  getOptionDisabled?: (option: any) => boolean;
  renderOption?: (props: object, option: any) => ReactNode;
  renderValue?: (values: any[]) => ReactNode;
  renderInput?: any;
  value?: any;
  onChange?: (event: SyntheticEvent<any>, value: any) => void;
  onClick?: () => void;
  filterOptions?: (options: Array<any>, state: object) => Array<any>;
  disableCloseOnSelect?: boolean;
  groupBy?: (options: any) => string;
  clearOnBlur?: boolean;
  noOptionsText?: ReactNode;
  renderGroup?: (params: any) => ReactNode;
  options: any[];
  slotProps?: MUIAutocompleteProps<any, any, any, any>['slotProps'];
  'data-cy'?: string;
};

type AutocompleteProps = CustomProps & AtomMUIProps;

const styles = {
  popper: {
    maxWidth: 'fit-content',
  },
  autocomplete: {
    '& .MuiAutocomplete-groupLabel': {
      color: colors.neutral.gray,
      textTransform: 'uppercase',
      fontSize: fonts.sm,
    },
    '& .MuiAutocomplete-clearIndicator': {
      padding: '4px',
    },
  },
  endAdornment: {
    '& .MuiAutocomplete-endAdornment': {
      top: 'calc(50% - 18px)',
    },
  },
};

export const Autocomplete = (props: AutocompleteProps) => {
  const {
    label,
    placeholder,
    startAdornment,
    endAdornment,
    disableClearable = false,
    clearIcon,
    popperAutoWidth,
    popperAnchorEl,
    popperWidth,
    autoFocus,
    error,
    errorHelperText,
    TextFieldProps,
    disableUnderline,
    multiple = false,
    sx,
    ...data
  } = props;

  const MUIProps: Omit<
    MUIAutocompleteProps<any, any, any, any>,
    'renderInput' | 'autoComplete'
  > = data;

  return (
    <MUIAutocomplete
      freeSolo
      multiple={multiple}
      disableClearable={disableClearable ?? !!endAdornment}
      {...((popperAutoWidth || popperAnchorEl || popperWidth) && {
        slots: {
          popper: (popperProps: any) => (
            <MUIPopper
              {...popperProps}
              {...(popperAnchorEl && { anchorEl: popperAnchorEl })}
              {...(popperAutoWidth && { placement: 'bottom-start' })}
              style={{
                ...(popperAutoWidth && styles.popper),
                ...(popperWidth && { width: popperWidth }),
              }}
            />
          ),
        },
      })}
      renderInput={params => (
        <TextField
          {...params}
          {...TextFieldProps}
          error={error}
          helperText={error && errorHelperText}
          autoFocus={autoFocus}
          label={label}
          placeholder={placeholder}
          inputLabelColor={colors.neutral.dim}
          slotProps={{
            htmlInput: params.slotProps.htmlInput,
            inputLabel: { ...params.slotProps.inputLabel },
            input: {
              ...(params.slotProps?.input ?? {}),
              ...(!!startAdornment && { startAdornment }),
              ...(!!endAdornment && { endAdornment }),
              ...(disableUnderline && { disableUnderline: true }),
            },
          }}
        />
      )}
      clearIcon={clearIcon || <Icon icon={CloseIcon} />}
      {...MUIProps}
      sx={[
        styles.autocomplete,
        !endAdornment && styles.endAdornment,
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      classes={props.classes}
    />
  );
};

export default Autocomplete;
